import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiPlus, FiSearch } from "react-icons/fi";
import { setRecipient } from "redux/slices/tradevuTransferSlice";
import OnboardingLayout from "component/auth/OnboardingLayout";
import PaymentStepper from "component/pay/PaymentStepper";
import { ProfileFilledIcon } from "asset/icons";
import axios from "axios";
import AddRecipientForm from "component/wallet/AddRecipientForm";
import { setBankRecipient } from "redux/slices/bankTransferSlice";
import { setCryptoFiatRecipient } from "../../../redux/slices/cryptoFiatTransferSlice";
import { ArrowRight2, Edit2, Location, Map1 } from "iconsax-reactjs";
import {
    setUsdAll,
    setUsdBeneficiaryId,
} from "../../../redux/slices/usdTransferSlice";
import CrossBorderRecipientForm from "component/wallet/CrossBorderRecipientForm";
import RecipientTypeModal from "component/pay/RecipientTypeModal";
import {
    patchCrossBorderTransfer,
    setCrossBorderUsdAll,
} from "../../../redux/slices/crossBorderTransferSlice";
import QuestionHelpIcon from "component/QuestionHelpIcon";
import FeatureTourPopover, { useFeatureTour } from "component/tour/FeatureTourPopover";

const getRecentTransactionId = (row) => row?._id || row?.transactionId || "";
const getBeneficiaryListId = (row) => row?._id || "";
const getUsdProviderId = (row) => row?.beneficiaryId || row?.bridgeBeneficiaryId || "";
const pickUsdIdFromRow = (row, isBeneficiariesTab) =>
    isBeneficiariesTab ? getBeneficiaryListId(row) : getUsdProviderId(row);
const pickSavedRecipientIds = (row, isBeneficiariesTab) => ({
    beneficiaryId: isBeneficiariesTab ? getBeneficiaryListId(row) : "",
    transactionId: isBeneficiariesTab ? "" : getRecentTransactionId(row),
});
const findUsdBeneficiaryId = (acct, code, partnersList = [], recentList = []) => {
    const byAcctBank = (arr) =>
        arr.find(
            (r) =>
                String(r.accountNumber) === String(acct) &&
                String(r.bankCode ?? r.accountCode ?? "") === String(code)
        );
    const hitFromBeneficiaries = byAcctBank(partnersList);
    if (hitFromBeneficiaries) return getBeneficiaryListId(hitFromBeneficiaries);
    const hitFromRecent = byAcctBank(recentList);
    if (hitFromRecent) return getUsdProviderId(hitFromRecent);
    return "";
};

const RECIPIENT_TO_ACCOUNT = {
    US: "us",
    NON_US: "iban",
    INTERNATIONAL_WIRE: "international_wire",
};

function RecipientDetails({ nextRoute }) {
    const [activeTab, setActiveTab] = useState("Recent recipients");
    const [selectedRecipient, setSelectedRecipient] = useState(null);
    const [loadingRecipients, setLoadingRecipients] = useState(true);
    const [partners, setPartners] = useState([]);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const isBankFlow = location.pathname.includes("bank-recipient");
    const isCryptoFlow = location.pathname.includes("crypto-wallet");
    const isCryptoToFiatFlow = location.pathname.includes("fiat-wallet");
    const isCrossBorder = location.pathname.includes("cross-border");
    const isSameCurrencyBankFlow = isBankFlow && !isCrossBorder;
    const isSameCurrencyTradevuFlow =
        !isBankFlow && !isCryptoFlow && !isCryptoToFiatFlow && !isCrossBorder;

    const { currency } = useSelector((state) => state);
    const cryptoFiat = useSelector((s) => s.cryptoFiatTransfer);
    const bankProvider = useSelector((s) => s.bankProvider);
    const provider = useSelector((s) => s.provider);
    const usdTransfer = useSelector((s) => s.usdTransfer);

    // Cross-border slice (read currencyTo from here)
    const crossBorder = useSelector((s) => s.crossBorderTransfer);

    const [search, setSearch] = useState("");
    const [view, setView] = useState("default");
    const [recipients, setRecipients] = useState([]);

    const { business } = useSelector((state) => state);
    const businessId = business?._id;

    const transferData = useSelector((state) => ({
        amount: state.amount,
        memo: state.memo,
        invoice: state.invoice,
        otherDocument: state.otherDocument,
        recipient: state.recipient,
        currency: state.currency,
    }));

    const savedRecipient = useSelector((s) =>
        isCrossBorder
            ? null
            : isCryptoToFiatFlow
            ? s.cryptoFiatTransfer?.recipient
            : isBankFlow
              ? s.bankRecipient
              : s.recipient
    );
    const cbCurrencyTo =
        (crossBorder && crossBorder.recipient && crossBorder.recipient.toCurrency) || "";
    const cbCurrencyFrom = (crossBorder && crossBorder.fromCurrency) || transferData.currency || "";
    const cryptoFiatCurrencyFrom = cryptoFiat?.currency || transferData.currency || "";
    const cryptoFiatCurrencyTo =
        cryptoFiat?.recipient?.toCurrency || cryptoFiat?.recipient?.currency || "";
    const queryCurrency = isCrossBorder
        ? String(cbCurrencyTo || "").toUpperCase()
        : isCryptoToFiatFlow
          ? String(cryptoFiatCurrencyTo || "").toUpperCase()
        : transferData.currency;

    const source = isCrossBorder
        ? crossBorder?.provider?.name || ""
        : isSameCurrencyBankFlow
          ? bankProvider?.name || ""
          : isSameCurrencyTradevuFlow
            ? provider?.name || ""
            : "";

    const isUsd = currency === "USD";
    const destinationCurrency = isCrossBorder
        ? String(cbCurrencyTo || "").toUpperCase()
        : String(transferData.currency || "").toUpperCase();
    const isCrossBorderUsd = isCrossBorder && destinationCurrency === "USD";
    const isOtcTransfer = Boolean(
        isCrossBorder &&
            crossBorder?.usePreferredRate &&
            Number(crossBorder?.preferredRate) > 0
    );
    const recipientChoiceTour = useFeatureTour(
        "step5",
        isOtcTransfer && view === "default" && !crossBorder?.isResumingOtcOffer
    );
    const showRecipientTypePicker = isBankFlow && destinationCurrency === "USD";

    const [showRecipientTypeModal, setShowRecipientTypeModal] = useState(false);
    const [recipientType, setRecipientType] = useState(() => {
        const savedLocation = sessionStorage.getItem("usdRecipientLocation");
        return ["US", "NON_US"].includes(savedLocation) ? savedLocation : null;
    });

    const isUsdTransfer = (isSameCurrencyBankFlow && isUsd) || isCrossBorderUsd;
    const selectedRecipientLocation =
        recipientType ||
        sessionStorage.getItem("usdRecipientLocation") ||
        sessionStorage.getItem("recipientType");
    const recipientLocationParam =
        isUsdTransfer && selectedRecipientLocation
            ? selectedRecipientLocation === "US"
                ? "US"
                : "nonUS"
            : "";
    const selectedPaymentRail = isCrossBorder
        ? crossBorder?.recipient?.usd?.paymentRail ||
          sessionStorage.getItem("paymentRail") ||
          sessionStorage.getItem("usdPaymentMethod")
        : usdTransfer?.paymentRail ||
          sessionStorage.getItem("paymentRail") ||
          sessionStorage.getItem("usdPaymentMethod");
    const paymentRailParam = isUsdTransfer ? selectedPaymentRail : "";

    useEffect(() => {
        if (!showRecipientTypePicker || !recipientType) return;
        const accountType = RECIPIENT_TO_ACCOUNT[recipientType] || "us";
        sessionStorage.setItem("recipientType", recipientType);
        sessionStorage.setItem("usdRecipientLocation", recipientType);
        if (isBankFlow && isUsd) {
            dispatch(setUsdAll({ accountType }));
        }
        if (isCrossBorderUsd) {
            dispatch(setCrossBorderUsdAll({ account_type: accountType }));
        }
    }, [showRecipientTypePicker, recipientType, dispatch, isBankFlow, isUsd, isCrossBorderUsd]);

    const recipientTypeLabel =
        {
            US: "Recipient in the US",
            NON_US: "Recipient outside the US",
            INTERNATIONAL_WIRE: "International wire recipient",
        }[recipientType] || "Select recipient type";

    useEffect(() => {
        if (!savedRecipient) return;

        const { accountIdentifier, accountNumber, accountName, bankName, institution, bankCode } =
            savedRecipient || {};

        const name = savedRecipient?.name || savedRecipient?.businessName || accountName || "";
        const number = accountNumber || accountIdentifier || "";
        const bank = bankName || savedRecipient?.bank_name || "";
        const code = bankCode || institution || "";

        if (!name && !number) return;

        const newKey = `${String(number)}|${String(code)}`;

        setSelectedRecipient((prev) => {
            const prevKey = prev
                ? `${String(prev.accountNumber)}|${String(prev.bankCode || "")}`
                : "";
            const keepPrevRaw = prev && prevKey === newKey;
            const rawFromStore = savedRecipient?.raw;

            return {
                name,
                accountNumber: number,
                bankName: bank,
                bankCode: code,
                swiftCode: savedRecipient?.swiftCode || "",
                currency: savedRecipient?.currency,
                beneficiaryId: savedRecipient?.beneficiaryId || "",
                transactionId: savedRecipient?.transactionId || "",
                bridgeBeneficiaryId: savedRecipient?.bridgeBeneficiaryId || "",
                raw: rawFromStore || (keepPrevRaw ? prev.raw : undefined),
            };
        });

        if (isUsd && isBankFlow) {
            const inferredId = findUsdBeneficiaryId(number, code, partners, recipients);
            dispatch(setUsdBeneficiaryId(inferredId || ""));
        }
    }, [savedRecipient, isBankFlow, isUsd, partners, recipients, dispatch]);

    const savedTabLabel =
        isBankFlow || isCryptoToFiatFlow || isCrossBorder ? "Beneficiaries" : "Partners";

    useEffect(() => {
        const fetchList = async () => {
            if (
                activeTab !==
                (isBankFlow || isCryptoToFiatFlow || isCrossBorder ? "Beneficiaries" : "Partners")
            )
                return;

            try {
                const endpoint =
                    isBankFlow || isCryptoToFiatFlow || isCrossBorder
                        ? "/v1/beneficiary/all"
                        : "/v1/wallets/network";

                const currencyFrom = isCrossBorder
                    ? String(cbCurrencyFrom || "").toUpperCase()
                    : isCryptoToFiatFlow
                      ? String(cryptoFiatCurrencyFrom || "").toUpperCase()
                    : String(transferData.currency || "").toUpperCase();
                const currencyTo = isCrossBorder
                    ? String(cbCurrencyTo || "").toUpperCase()
                    : isCryptoToFiatFlow
                      ? String(cryptoFiatCurrencyTo || "").toUpperCase()
                    : currencyFrom;

                const params =
                    isBankFlow || isCryptoToFiatFlow || isCrossBorder
                        ? {
                              currency: currencyFrom,
                              currencyTo,
                              limit: 100,
                              ...(recipientLocationParam
                                  ? { location: recipientLocationParam }
                                  : {}),
                              ...(paymentRailParam ? { paymentRail: paymentRailParam } : {}),
                          }
                        : { currency: currencyFrom, currencyTo };

                if (source) params.source = source;

                const res = await axios.get(endpoint, {
                    params,
                    headers: { "x-business-id": businessId },
                });

                setPartners(res.data?.data || []);
            } catch (err) {
                console.error("Failed to fetch list", err);
                setPartners([]);
            }
        };

        fetchList();
    }, [
        activeTab,
        transferData.currency,
        businessId,
        isBankFlow,
        isCryptoToFiatFlow,
        isCrossBorder,
        source,
        queryCurrency,
        cbCurrencyFrom,
        cbCurrencyTo,
        cryptoFiatCurrencyFrom,
        cryptoFiatCurrencyTo,
        recipientLocationParam,
        paymentRailParam,
    ]);

    useEffect(() => {
        const controller = new AbortController();
        const fetchRecipients = async () => {
            try {
                setLoadingRecipients(true);

                const transferType = isCryptoToFiatFlow
                    ? "CryptoTransfer"
                    : isCrossBorder || (isBankFlow && isUsd)
                      ? "FxTransfer"
                      : isBankFlow
                        ? "BankTransfer"
                        : "TradevutoTradevu";

                const currencyFrom = isCrossBorder
                    ? String(cbCurrencyFrom || "").toUpperCase()
                    : isCryptoToFiatFlow
                      ? String(cryptoFiatCurrencyFrom || "").toUpperCase()
                    : String(transferData.currency || "").toUpperCase();
                const currencyTo = isCrossBorder
                    ? String(cbCurrencyTo || "").toUpperCase()
                    : isCryptoToFiatFlow
                      ? String(cryptoFiatCurrencyTo || "").toUpperCase()
                    : currencyFrom;

                const res = await axios.get("/v1/wallets/latest_transaction", {
                    params: {
                        currency: currencyFrom,
                        currencyTo,
                        type: transferType,
                        ...(recipientLocationParam
                            ? { location: recipientLocationParam }
                            : {}),
                        ...(paymentRailParam ? { paymentRail: paymentRailParam } : {}),
                        ...(source ? { source } : {}),
                    },
                    headers: { "x-business-id": businessId },
                    signal: controller.signal,
                });

                const list = res.data?.data || [];
                const filtered = isCryptoToFiatFlow
                    ? list.filter((t) => {
                          const sub =
                              t?.TransactionSubType ??
                              t?.transactionSubType ??
                              t?.subType ??
                              t?.subtype;
                          return String(sub).toUpperCase() === "TOFIAT";
                      })
                    : list;

                setRecipients(filtered);
            } catch (error) {
                console.error("Failed to fetch recipients", error);
                setRecipients([]);
            } finally {
                setLoadingRecipients(false);
            }
        };

        fetchRecipients();
        return () => controller.abort();
    }, [
        queryCurrency,
        businessId,
        isBankFlow,
        isCryptoToFiatFlow,
        isCrossBorder,
        isUsd,
        cbCurrencyFrom,
        cbCurrencyTo,
        cryptoFiatCurrencyFrom,
        cryptoFiatCurrencyTo,
        transferData.currency,
        source,
        recipientLocationParam,
        paymentRailParam,
    ]);

    const filteredList =
        activeTab === savedTabLabel
            ? partners.filter((p) =>
                  (p.businessName || p.name || "").toLowerCase().includes(search.toLowerCase())
              )
            : recipients.filter((r) =>
                  (r.businessName || r.name || "").toLowerCase().includes(search.toLowerCase())
              );

    const populateCrossBorderRecipient = (selection) => {
        if (!isCrossBorder || !selection) return;

        const row = selection.raw || selection;
        const fields = {
            accountNumber:
                selection.accountNumber ||
                row.accountNumber ||
                row?.account?.account_number ||
                row?.iban?.account_number ||
                "",
            accountName:
                selection.name ||
                row.businessName ||
                row.name ||
                row.accountName ||
                row.account_owner_name ||
                "",
            bankCode:
                selection.bankCode || row.bankCode || row.accountCode || row.institution || "",
            bankName: selection.bankName || row.bankName || row.bank_name || "",
            branchCode: row.branchCode || "",
            iban: row.iban?.account_number || row.iban || "",
            swiftBic: row.swiftCode || row.swiftBic || row.swift || row.bic || "",
            sortCode: row.sortCode || "",
            transitNumber: row.transitNumber || "",
            institutionNumber: row.institutionNumber || "",
            routingNumber: row.routingNumber || row?.account?.routing_number || "",
        };

        dispatch(
            patchCrossBorderTransfer({
                recipientSkipped: false,
                recipient: {
                    bankName: fields.bankName,
                    country: row.country || "",
                    beneficiaryId: selection.beneficiaryId || "",
                    transactionId: selection.transactionId || "",
                    fields,
                },
            })
        );
    };

    const handleContinue = () => {
        if (!selectedRecipient) return;

        populateCrossBorderRecipient(selectedRecipient);

        if (isCrossBorderUsd) {
            const row = selectedRecipient.raw || selectedRecipient;
            const accountType = row.account_type || row.accountType || "us";
            dispatch(
                setCrossBorderUsdAll({
                    ...row,
                    bank_name: row.bank_name || row.bankName || selectedRecipient.bankName || "",
                    account_type: accountType,
                    paymentRail:
                        row.paymentTrail ||
                        row.paymentRail ||
                        row.payment_rail ||
                        row.paymentMethod ||
                        (accountType === "us" ? "ach" : "swift"),
                    location:
                        row.location ||
                        row.recipient_location ||
                        row.recipientLocation ||
                        (accountType === "us" ? "US" : "nonUS"),
                    account_owner_name:
                        row.account_owner_name ||
                        row.accountName ||
                        selectedRecipient.name ||
                        "",
                    account:
                        row.account ||
                        (accountType === "us"
                            ? {
                                  account_number: selectedRecipient.accountNumber || "",
                                  routing_number: row.routingNumber || "",
                              }
                            : undefined),
                    iban: row.iban,
                    swift: row.swift,
                })
            );
        }

        if (isBankFlow) {
            dispatch(
                setBankRecipient({
                    name: selectedRecipient.name,
                    accountNumber: selectedRecipient.accountNumber,
                    bankName: selectedRecipient.bankName ?? "",
                    bankCode: selectedRecipient.bankCode ?? "",
                    swiftCode: selectedRecipient.swiftCode ?? "",
                    beneficiaryId: selectedRecipient.beneficiaryId ?? "",
                    transactionId: selectedRecipient.transactionId ?? "",
                })
            );
        }

        if (isUsd && isBankFlow) {
            const rowFromSelection = selectedRecipient?.raw;

            const rowFallback =
                recipients.find((r) => {
                    const acct =
                        r.accountNumber ||
                        r?.account?.account_number ||
                        r?.iban?.account_number ||
                        r?.swift?.account_number ||
                        "";
                    const bank = r.bankName || r.bank_name || "";
                    return (
                        String(acct) === String(selectedRecipient.accountNumber) &&
                        String(bank) === String(selectedRecipient.bankName || "")
                    );
                }) || null;

            const row = rowFromSelection || rowFallback || {};

            const sesid = row.sessionId || row.sessionID || row.sesid || "";
            const selectedIsRecentRecipient = !!selectedRecipient?.transactionId;
            const selectedBeneficiaryId = selectedRecipient?.beneficiaryId || "";
            const selectedTransactionId = selectedRecipient?.transactionId || "";

            dispatch(
                setUsdAll({
                    ...row,
                    currency: "USD",
                    currencyFrom: "USD",
                    sesid,
                    beneficiaryId:
                        selectedBeneficiaryId ||
                        (!selectedIsRecentRecipient
                            ? row.beneficiaryId || row.bridgeBeneficiaryId
                            : "") ||
                        "",
                    transactionId: selectedTransactionId,
                    bridgeBeneficiaryId:
                        row.bridgeBeneficiaryId || row.beneficiaryId || "",
                })
            );

            dispatch(
                setUsdBeneficiaryId(
                    selectedBeneficiaryId ||
                        (!selectedIsRecentRecipient
                            ? row.beneficiaryId || row.bridgeBeneficiaryId
                            : "") ||
                        ""
                )
            );
        }

        if (isCryptoToFiatFlow) {
            dispatch(
                setCryptoFiatRecipient({
                    institution: selectedRecipient.bankCode ?? selectedRecipient.accountCode ?? "",
                    bankName: selectedRecipient.bankName ?? "",
                    accountIdentifier: selectedRecipient.accountNumber,
                    accountName: selectedRecipient.name,
                    toCurrency: selectedRecipient.currency || cryptoFiatCurrencyTo,
                })
            );
        } else if (!isCrossBorder) {
            dispatch(
                setRecipient({
                    name: selectedRecipient.name,
                    accountNumber: selectedRecipient.accountNumber,
                })
            );
        }

        navigate(nextRoute);
    };

    const hasSelectedRecipient =
        !!selectedRecipient &&
        (!!String(selectedRecipient?.accountNumber || "").trim() ||
            !!String(selectedRecipient?.name || "").trim());

    const handleBackToPartners = (tabName = "Partners") => {
        setActiveTab(tabName);
        setView("default");
    };

    const pageTitle =
        view === "add" && recipientType === "US"
            ? "Send to recipient in the US"
            : view === "add" && recipientType === "NON_US"
              ? "Send to recipient outside the US"
              : "Send money";

    return (
        <OnboardingLayout title={pageTitle} showSkip={false}>
            <div className="max-w-3xl  px-4 md:mx-auto mt-10 space-y-10">
                <PaymentStepper currentStep={1} />

                {view === "default" ? (
                    <>
                        <div>
                            <h1 className="text-2xl font-normal text-[#101828]">
                                Who are you sending to?
                            </h1>
                            {isOtcTransfer && !crossBorder?.isResumingOtcOffer && (
                                <div
                                    className="mt-2 flex w-fit max-w-full flex-wrap items-center gap-x-1 gap-y-1 aku-body-small-regular !text-[#64748B]"
                                    data-feature-tour="otc-recipient-choice"
                                >
                                    <span>You can add them now or</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            dispatch(
                                                patchCrossBorderTransfer({
                                                    recipientSkipped: true,
                                                })
                                            );
                                            navigate(
                                                "/payment/send-money/cross-border/bank-recipient/review-payment-details"
                                            );
                                        }}
                                        className="font-[450] !text-[#6F00FF]"
                                    >
                                        skip to summary
                                    </button>
                                    <QuestionHelpIcon
                                        size={18}
                                        details={
                                            <span>
                                                Adding now means we can send immediately a rate is
                                                approved.
                                                <br />
                                                Skipping means you&apos;ll provide them once a rate is
                                                agreed.
                                            </span>
                                        }
                                    />
                                </div>
                            )}
                        </div>

                        {hasSelectedRecipient ? (
                            <div className="flex items-center justify-between !p-4 bg-white rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className="h-14 w-14 rounded-full bg-[#F4F5FF] flex items-center justify-center">
                                        <span className="text-[#383B56] text-[16px] font-[450]">
                                            {(selectedRecipient.name || "")
                                                .split(" ")
                                                .map((p) => p?.[0])
                                                .join("")
                                                .slice(0, 2)
                                                .toUpperCase() || "•"}
                                        </span>
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[#161827] text-[16px] font-[450] truncate">
                                            {selectedRecipient.name || "—"}
                                        </p>
                                        <p className="text-[#6F739B] font-[350] !text-[14px] truncate">
                                            {(() => {
                                                if (isCryptoFlow) {
                                                    return selectedRecipient.accountNumber || "—";
                                                }
                                                if (isCryptoToFiatFlow) {
                                                    const bank = selectedRecipient.bankName || "";
                                                    const acct =
                                                        selectedRecipient.accountNumber || "";
                                                    return bank && acct
                                                        ? `${bank} • ${acct}`
                                                        : acct || bank || "—";
                                                }
                                                if (isBankFlow) {
                                                    const bank = selectedRecipient.bankName || "";
                                                    const acct =
                                                        selectedRecipient.accountNumber || "";
                                                    return bank && acct
                                                        ? `${bank} • ${acct}`
                                                        : acct || bank || "—";
                                                }
                                                return selectedRecipient.accountNumber || "—";
                                            })()}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setView("add")}
                                    className="group inline-flex items-center gap-2 text-[#6F00FF] hover:opacity-90"
                                    aria-label="Edit recipient"
                                >
                                    <Edit2 size="22" color="#6F00FF" />
                                    <span className="sr-only">Edit</span>
                                </button>
                            </div>
                        ) : !showRecipientTypePicker || recipientType ? (
                            <div
                                className="flex items-center justify-between p-4 bg-white rounded-xl cursor-pointer"
                                onClick={() => setView("add")}
                            >
                                <div className="flex items-center gap-3">
                                    <ProfileFilledIcon />
                                    <span className="text-[#344054] font-medium">
                                        Add new recipient
                                    </span>
                                </div>
                                <FiPlus color="#6F00FF" />
                            </div>
                        ) : null}

                        {showRecipientTypePicker && !recipientType && (
                            <div
                                className="flex items-center justify-between p-4 bg-white rounded-xl cursor-pointer mt-4"
                                onClick={() => setShowRecipientTypeModal(true)}
                            >
                                <div className="flex items-center gap-3">
                                    <ProfileFilledIcon />
                                    <div className="flex flex-col">
                                        <span className="text-[#344054] font-medium">
                                            Choose recipient type
                                        </span>
                                        {recipientTypeLabel !== "Select recipient type" && (
                                            <span className="text-[#6F739B] text-[14px] font-[350]">
                                                {recipientTypeLabel}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <ArrowRight2 size={20} color="#6F00FF" />
                            </div>
                        )}

                        {/* Tabs */}
                        {!isCryptoFlow && (
                            <div className="block md:flex gap-6 items-center justify-between">
                                <div className="flex gap-4 mb-4 md:mb-0">
                                    {[
                                        "Recent recipients",
                                        isBankFlow || isCryptoToFiatFlow || isCrossBorder
                                            ? "Beneficiaries"
                                            : "Partners",
                                    ].map((tab) => (
                                        <button
                                            key={tab}
                                            onClick={() => setActiveTab(tab)}
                                            className={`font-normal text-normal ${
                                                activeTab === tab
                                                    ? "text-[#161827] px-4 py-1 rounded-full bg-white"
                                                    : "text-[#4F5372]"
                                            }`}
                                        >
                                            {tab}
                                        </button>
                                    ))}
                                </div>

                                <div className="relative w-80">
                                    <input
                                        type="text"
                                        placeholder="Search by name"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="w-full !shadow-none rounded-lg px-10 py-2 text-sm bg-[#F2F3FC] focus:outline-none"
                                    />
                                    <FiSearch className="absolute left-3 top-3 text-[#667085]" />
                                </div>
                            </div>
                        )}

                        {!isCryptoFlow && (
                            <div className="bg-white p-2">
                                {loadingRecipients ? (
                                    <p className="text-center text-[#667085] text-sm py-6">
                                        Loading…
                                    </p>
                                ) : filteredList.length === 0 ? (
                                    <p className="text-center text-[#667085] text-sm py-6">
                                        {activeTab === savedTabLabel
                                            ? isBankFlow || isCryptoToFiatFlow || isCrossBorder
                                                ? "No beneficiaries available."
                                                : "No partners available."
                                            : "No recent recipients found."}
                                    </p>
                                ) : (
                                    filteredList.map((recipient) => {
                                        const isListTab = activeTab === savedTabLabel;

                                        const resolvedName =
                                            (isListTab
                                                ? recipient.name ||
                                                  recipient.businessName ||
                                                  recipient.account_owner_name
                                                : recipient.businessName ||
                                                  recipient.name ||
                                                  recipient.account_owner_name) || "";

                                        const resolvedAccountNumber =
                                            recipient.accountNumber ||
                                            recipient?.account?.account_number ||
                                            recipient?.iban?.account_number ||
                                            recipient?.swift?.account_number ||
                                            "";

                                        // --- Bank name / code ---
                                        const resolvedBankName =
                                            recipient.bankName ?? recipient.bank_name ?? "";
                                        const resolvedBankCode =
                                            recipient.bankCode ?? recipient.accountCode ?? "";

                                        const isSelected =
                                            String(selectedRecipient?.accountNumber || "") ===
                                            String(resolvedAccountNumber);

                                        const initials = (resolvedName || "•")
                                            .split(" ")
                                            .filter(Boolean)
                                            .map((n) => n[0])
                                            .join("")
                                            .slice(0, 2)
                                            .toUpperCase();

                                        const rowKey =
                                            resolvedAccountNumber ||
                                            recipient?._id ||
                                            `${resolvedName}-${resolvedBankName}-${Math.random().toString(16).slice(2)}`;

                                        return (
                                            <div
                                                key={rowKey}
                                                onClick={() => {
                                                    const payload = {
                                                        name: resolvedName,
                                                        accountNumber: resolvedAccountNumber,
                                                        bankName: resolvedBankName,
                                                        bankCode: resolvedBankCode,
                                                        swiftCode: recipient.swiftCode ?? "",
                                                        currency: recipient.currency,
                                                        ...pickSavedRecipientIds(
                                                            recipient,
                                                            isListTab
                                                        ),
                                                        bridgeBeneficiaryId:
                                                            getUsdProviderId(recipient),
                                                        raw: recipient, // ✅ critical for USD latest dispatch
                                                    };

                                                    setSelectedRecipient(payload);

                                                    if (isCrossBorder) {
                                                        populateCrossBorderRecipient(payload);
                                                    } else if (isBankFlow) {
                                                        dispatch(setBankRecipient(payload)); // raw is preserved by your updated effect
                                                    } else if (isCryptoToFiatFlow) {
                                                        dispatch(
                                                            setCryptoFiatRecipient({
                                                                institution: payload.bankCode,
                                                                bankName: payload.bankName,
                                                                accountIdentifier:
                                                                    payload.accountNumber,
                                                                accountName: payload.name,
                                                                toCurrency:
                                                                    payload.currency ||
                                                                    cryptoFiatCurrencyTo,
                                                            })
                                                        );
                                                    } else {
                                                        dispatch(
                                                            setRecipient({
                                                                name: payload.name,
                                                                accountNumber:
                                                                    payload.accountNumber,
                                                            })
                                                        );
                                                    }

                                                    if (isUsd && isBankFlow) {
                                                        dispatch(
                                                            setUsdBeneficiaryId(
                                                                payload.beneficiaryId || ""
                                                            )
                                                        );
                                                    }
                                                }}
                                                className={`
        flex items-center justify-between !mt-0 bg-white p-3 rounded-lg cursor-pointer transition
        ${isSelected ? "border-1 border-solid !border-[#6F00FF]" : ""}
      `}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-[#EAECF0] flex items-center justify-center font-medium text-[#667085] text-sm">
                                                        {initials}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p
                                                            className={`font-medium truncate ${
                                                                isSelected
                                                                    ? "text-[#6F00FF]"
                                                                    : "text-[#101828]"
                                                            }`}
                                                        >
                                                            {resolvedName || "—"}
                                                        </p>
                                                        <p className="text-[#667085] text-sm truncate">
                                                            {resolvedAccountNumber || "—"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {/* Footer */}
                        <div className="flex gap-4 justify-end mt-10">
                            <button
                                className="text-[#667085] font-medium"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                onClick={handleContinue}
                                disabled={!selectedRecipient}
                                className={`px-6 py-2 rounded-lg font-semibold text-white transition ${
                                    selectedRecipient
                                        ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                        : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                                }`}
                            >
                                Continue
                            </button>
                        </div>
                    </>
                ) : isCrossBorder ? (
                    <CrossBorderRecipientForm onBack={handleBackToPartners} nextRoute={nextRoute} />
                ) : (
                    <AddRecipientForm onBack={handleBackToPartners} nextRoute={nextRoute} />
                )}
            </div>

            <RecipientTypeModal
                open={showRecipientTypeModal}
                active={recipientType}
                title="Recipient bank location"
                options={[
                    {
                        id: "US",
                        title: "Recipient bank in the US",
                        desc: "Send using routing and account numbers to US-based banks",
                        icon: <Location />,
                    },
                    {
                        id: "NON_US",
                        title: "Recipient bank outside the US",
                        desc: "Send using IBAN to banks outside the US",
                        icon: <Map1 />,
                    },
                ]}
                onSelect={(id) => {
                    setRecipientType(id);
                    setView("add");
                    setShowRecipientTypeModal(false);
                }}
                onClose={() => setShowRecipientTypeModal(false)}
            />

            <FeatureTourPopover
                open={recipientChoiceTour.isOpen}
                target='[data-feature-tour="otc-recipient-choice"]'
                title="You can add now or skip for later"
                description={
                    <>
                        <strong className="font-semibold text-[#101828]">Add now</strong> so we can
                        send once a rate is approved, or{" "}
                        <strong className="font-semibold text-[#101828]">skip</strong> to get an
                        offer before completing the transfer.
                    </>
                }
                secondaryLabel="Learn more"
                primaryLabel="Got it"
                placement="right"
                alignment="start"
                onSecondary={recipientChoiceTour.dismiss}
                onPrimary={recipientChoiceTour.dismiss}
            />
        </OnboardingLayout>
    );
}

export default RecipientDetails;
